import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, PhoneCall, PhoneMissed, ArrowUpRight } from "lucide-react";
import { api, formatApiError, STAGE_LABEL } from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function dueLabel(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(Math.abs(diff) / 3600000);
  const m = Math.floor((Math.abs(diff) / 60000) % 60);
  const span = h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h ${m}m`;
  return diff < 0 ? { text: `in ${span}`, tone: "text-forest/50" } : { text: `${span} overdue`, tone: "text-clay font-medium" };
}

const CallBtn = ({ leadId, label, testid }) => {
  const [busy, setBusy] = useState(false);
  const call = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/call`);
      toast.success(data.mock ? "Call logged · CallerDesk keys not set" : "Calling — your phone will ring now");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Call failed"); }
    setBusy(false);
  };
  return (
    <button data-testid={testid} disabled={busy} onClick={call}
      className="h-8 px-3 shrink-0 inline-flex items-center gap-1.5 rounded-sm bg-forest text-white text-xs font-medium hover:bg-forest-soft disabled:opacity-60 transition-colors duration-150">
      <PhoneCall className="h-3.5 w-3.5" /> {busy ? "Calling…" : label}
    </button>
  );
};

const Row = ({ item, time, sub, note, action }) => (
  <div data-testid={`action-row-${item.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-bone-alt/30 transition-colors duration-100">
    <div className="w-16 sm:w-28 shrink-0 text-xs font-display font-bold text-forest tabular-nums">{time}</div>
    <a href={`/leads/${item.lead_id}`} target="_blank" rel="noreferrer" data-testid={`action-lead-link-${item.id}`} className="flex-1 min-w-0 group">
      <div className="text-sm font-medium text-forest truncate group-hover:underline underline-offset-2 inline-flex items-center gap-1 max-w-full">
        <span className="truncate">{item.lead_name || "Lead"}</span><ArrowUpRight className="h-3 w-3 text-forest/40 shrink-0" />
      </div>
      <div className="text-[11px] text-forest/55 truncate">{sub}</div>
      {note && <div className={`sm:hidden text-[11px] ${note.tone}`}>{note.text}</div>}
    </a>
    {note && <div className={`hidden sm:block text-xs shrink-0 ${note.tone}`}>{note.text}</div>}
    {action}
  </div>
);

const VIEWS = {
  calls: { title: "Follow-up calls due", empty: "No calls due in this period.", icon: Phone },
  missed: { title: "Missed calls", empty: "No missed calls in this period.", icon: PhoneMissed },
  tasks: { title: "Tasks", empty: "No meetings, emails or WhatsApp tasks in this period.", icon: Phone },
};

export const ActionListPanel = ({ view, data }) => {
  const v = VIEWS[view];
  const items = view === "calls" ? data.due_calls || [] : view === "missed" ? data.missed_call_list || [] : data.todo_list || [];
  const total = { calls: data.widgets.follow_up_calls, missed: data.widgets.missed_calls, tasks: data.widgets.tasks }[view] ?? items.length;
  const sub = (i) => [i.lead_phone, STAGE_LABEL[i.lead_stage]].filter(Boolean).join(" · ");
  return (
    <div data-testid={`action-panel-${view}`} className="border border-[#E6E4DD] bg-white rounded-sm">
      <div className="px-5 py-3 border-b border-[#E6E4DD] flex items-center justify-between bg-bone-alt/40">
        <div className="font-display font-bold text-base text-forest tracking-tight">{v.title}</div>
        <div data-testid="action-panel-count" className="text-xs text-forest/50">
          {total > items.length ? <>Showing {items.length} of {total} · <a href="/follow-ups" className="underline underline-offset-2 hover:text-forest">See all</a></> : items.length}
        </div>
      </div>
      <div className="divide-y divide-[#E6E4DD] max-h-[340px] overflow-y-auto">
        {items.map((i) => view === "calls" ? (
          <Row key={i.id} item={i} time={fmt(i.due_at)} sub={[sub(i), i.notes].filter(Boolean).join(" · ")} note={dueLabel(i.due_at)}
            action={<CallBtn leadId={i.lead_id} label="Call now" testid={`call-now-${i.id}`} />} />
        ) : view === "missed" ? (
          <Row key={i.id} item={i} time={fmt(i.created_at)} sub={[sub(i), i.meta?.direction === "incoming" ? "Incoming · missed" : "Not answered"].filter(Boolean).join(" · ")}
            action={<CallBtn leadId={i.lead_id} label="Call back" testid={`call-back-${i.id}`} />} />
        ) : (
          <Row key={i.id} item={i} time={fmt(i.due_at)} sub={[i.kind?.toUpperCase(), i.notes].filter(Boolean).join(" · ")} note={dueLabel(i.due_at)} />
        ))}
        {items.length === 0 && <div className="px-5 py-6 text-sm text-forest/50">{v.empty}</div>}
      </div>
    </div>
  );
};
